import { supabase } from '@/lib/supabase'
import type { RewardsTrialAttempt, RewardsExchange, RewardsAccountExchangeLink, RewardsCycleExchangeMetric } from '../types'
import { getOpenRewardsCycle, listRewardsCycleMetrics } from './rewardsCyclesService'

export async function listAllRewardsLinks(): Promise<RewardsAccountExchangeLink[]> {
  const { data, error } = await supabase
    .from('rewards_account_exchange_links')
    .select('id,account_id,exchange,exchange_user_id,status,linked_at,last_sync_at,last_error,created_at,updated_at')
    .order('created_at', { ascending: false })
    .limit(500)
  if (error) throw error
  return ((data || []) as any[]).map((r) => ({
    id: r.id,
    account_id: r.account_id,
    exchange: r.exchange,
    exchange_user_id: r.exchange_user_id,
    status: r.status,
    linked_at: r.linked_at || null,
    last_sync_at: r.last_sync_at || null,
    last_error: r.last_error || null,
    created_at: r.created_at,
    updated_at: r.updated_at,
  }))
}

export async function updateRewardsLinkStatus(params: {
  id: string
  status: RewardsAccountExchangeLink['status']
  linkedAt?: string | null
}): Promise<void> {
  const patch: any = { status: params.status }
  if (params.linkedAt) patch.linked_at = params.linkedAt
  const { error } = await supabase.from('rewards_account_exchange_links').update(patch).eq('id', params.id)
  if (error) throw error
}

export async function listActiveRewardsTrialAttempts(): Promise<RewardsTrialAttempt[]> {
  const { data, error } = await supabase
    .from('rewards_trial_attempts')
    .select('*')
    .in('status', ['active', 'failed', 'approved'])
    .order('check_deadline_at', { ascending: false })
    .limit(200)
  if (error) throw error
  return ((data || []) as any[]).map((r) => ({
    id: r.id,
    user_id: r.user_id,
    account_id: r.account_id,
    status: r.status,
    selected_plan_id: r.selected_plan_id || null,
    stripe_customer_id: r.stripe_customer_id || null,
    stripe_subscription_id: r.stripe_subscription_id || null,
    started_at: r.started_at,
    check_deadline_at: r.check_deadline_at,
    trial_end_at: r.trial_end_at || null,
    min_volume_usdt: Number(r.min_volume_usdt || 0),
    volume_usdt_at_deadline: Number(r.volume_usdt_at_deadline || 0),
    passed: (r.passed as any) ?? null,
    evaluated_at: r.evaluated_at || null,
    fail_reason: r.fail_reason || null,
    override_volume_usdt: r.override_volume_usdt != null ? Number(r.override_volume_usdt) : null,
    override_passed: (r.override_passed as any) ?? null,
    override_note: r.override_note || null,
    overridden_by: r.overridden_by || null,
    overridden_at: r.overridden_at || null,
    created_at: r.created_at,
    updated_at: r.updated_at,
  }))
}

export async function setRewardsTrialOverride(params: {
  id: string
  overridePassed: boolean
  overrideVolumeUsdt: number
  note?: string | null
  overriddenBy?: string | null
}): Promise<void> {
  const { error } = await supabase
    .from('rewards_trial_attempts')
    .update({
      override_passed: params.overridePassed,
      override_volume_usdt: Math.max(0, Number(params.overrideVolumeUsdt || 0)),
      override_note: (params.note || '').trim() || null,
      overridden_by: params.overriddenBy || null,
      overridden_at: new Date().toISOString(),
    } as any)
    .eq('id', params.id)
  if (error) throw error
}

export async function setManualCommissionConfirmed(params: {
  accountId: string
  exchange: RewardsExchange
  commissionConfirmedUsdt: number
  volumeUsdt?: number | null
  updatedBy?: string | null
}): Promise<{ cycleId: string; metrics: RewardsCycleExchangeMetric[] }> {
  const cycle = await getOpenRewardsCycle(params.accountId)
  if (!cycle) throw new Error('Ciclo aberto não encontrado (execute o sync ou aguarde)')

  const patch: any = {
    commission_confirmed_usdt: Math.max(0, Number(params.commissionConfirmedUsdt || 0)),
    source: 'manual',
    updated_by: params.updatedBy || null,
    updated_at: new Date().toISOString(),
  }
  if (params.volumeUsdt != null) patch.volume_usdt = Math.max(0, Number(params.volumeUsdt || 0))

  const { error } = await supabase
    .from('rewards_cycle_exchange_metrics')
    .update(patch)
    .eq('cycle_id', cycle.id)
    .eq('exchange', params.exchange)
  if (error) throw error

  const metrics = await listRewardsCycleMetrics(cycle.id)
  return { cycleId: cycle.id, metrics }
}
